import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Bell, BellOff, Heart, Sparkles, MessageCircle, ThumbsUp, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert base64 URL to Uint8Array for push subscription
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function NotificationSettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  
  const [preferences, setPreferences] = useState({
    push_enabled: false,
    endorsements: true,
    shoutouts: true,
    comments: true,
    reactions: true
  });

  useEffect(() => {
    // Check if push is supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setPushSupported(supported);
    
    if (supported) {
      setPushPermission(Notification.permission);
    }
    
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const res = await fetch(`${API}/notifications/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPreferences(data);
      }
    } catch (e) {
      console.error('Error fetching preferences:', e);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async (newPrefs) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newPrefs)
      });
      if (res.ok) {
        setPreferences(newPrefs);
        toast.success('Preferences saved!');
      }
    } catch (e) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const enablePushNotifications = async () => {
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      
      if (permission !== 'granted') {
        toast.error('Notification permission denied');
        return;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw-push.js');
      await navigator.serviceWorker.ready;

      // Get VAPID public key
      const vapidRes = await fetch(`${API}/push/vapid-key`);
      const { publicKey } = await vapidRes.json();

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to server
      const subRes = await fetch(`${API}/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
          }
        })
      });

      if (subRes.ok) {
        setPreferences({ ...preferences, push_enabled: true });
        toast.success('Push notifications enabled!');
        
        // Send test notification
        await fetch(`${API}/push/test`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (e) {
      console.error('Error enabling push:', e);
      toast.error('Failed to enable push notifications');
    }
  };

  const disablePushNotifications = async () => {
    try {
      // Unsubscribe on server
      await fetch(`${API}/push/subscribe`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      // Unsubscribe from service worker
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }

      setPreferences({ ...preferences, push_enabled: false });
      toast.success('Push notifications disabled');
    } catch (e) {
      toast.error('Failed to disable push notifications');
    }
  };

  const handleToggle = (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    savePreferences(newPrefs);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-2xl mx-auto px-4 md:px-8">
        <h1 className="text-3xl font-heading font-extrabold flex items-center gap-3 mb-8">
          <Bell className="w-8 h-8" />
          Notification Settings
        </h1>

        <div className="space-y-6">
          {/* Push Notifications Master Toggle */}
      <div className="card-brutal p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            {preferences.push_enabled ? (
              <div className="p-2 rounded-full bg-pastel-crochet/20">
                <Bell className="w-5 h-5 text-pastel-crochet" />
              </div>
            ) : (
              <div className="p-2 rounded-full bg-muted">
                <BellOff className="w-5 h-5 text-muted-foreground" />
              </div>
            )}
            <div>
              <h3 className="font-heading font-bold">Push Notifications</h3>
              <p className="text-sm text-muted-foreground">
                Get notified even when you're not on Gathered Things
              </p>
            </div>
          </div>
          
          {pushSupported ? (
            preferences.push_enabled ? (
              <Button 
                variant="outline" 
                onClick={disablePushNotifications}
                className="btn-brutal"
              >
                Disable
              </Button>
            ) : (
              <Button 
                onClick={enablePushNotifications}
                className="btn-brutal bg-pastel-crochet hover:bg-pastel-crochet/90"
                disabled={pushPermission === 'denied'}
              >
                Enable
              </Button>
            )
          ) : (
            <span className="text-sm text-muted-foreground">Not supported in this browser</span>
          )}
        </div>
        
        {pushPermission === 'denied' && (
          <p className="mt-3 text-sm text-destructive">
            Notifications are blocked. Please enable them in your browser settings.
          </p>
        )}
      </div>

      {/* Notification Type Toggles */}
      <div className="card-brutal p-6 space-y-5">
        <h3 className="font-heading font-bold">Notification Types</h3>
        <p className="text-sm text-muted-foreground -mt-2">
          Choose which notifications you want to receive
        </p>
        
        <div className="space-y-4 pt-2">
          {/* Endorsements */}
          <div className="flex items-center justify-between" data-testid="toggle-endorsements">
            <div className="flex items-center gap-3">
              <Heart className="w-5 h-5 text-pastel-crochet" />
              <div>
                <Label htmlFor="endorsements" className="font-medium">Endorsements</Label>
                <p className="text-sm text-muted-foreground">When someone endorses your store</p>
              </div>
            </div>
            <Switch
              id="endorsements"
              checked={preferences.endorsements}
              onCheckedChange={(v) => handleToggle('endorsements', v)}
            />
          </div>

          {/* Shoutouts */}
          <div className="flex items-center justify-between" data-testid="toggle-shoutouts">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-yellow-500" />
              <div>
                <Label htmlFor="shoutouts" className="font-medium">Shoutouts</Label>
                <p className="text-sm text-muted-foreground">When someone welcomes you to the community</p>
              </div>
            </div>
            <Switch
              id="shoutouts"
              checked={preferences.shoutouts}
              onCheckedChange={(v) => handleToggle('shoutouts', v)}
            />
          </div>

          {/* Comments */}
          <div className="flex items-center justify-between" data-testid="toggle-comments">
            <div className="flex items-center gap-3">
              <MessageCircle className="w-5 h-5 text-blue-500" />
              <div>
                <Label htmlFor="comments" className="font-medium">Comments</Label>
                <p className="text-sm text-muted-foreground">When someone comments on your post</p>
              </div>
            </div>
            <Switch
              id="comments"
              checked={preferences.comments}
              onCheckedChange={(v) => handleToggle('comments', v)}
            />
          </div>

          {/* Reactions */}
          <div className="flex items-center justify-between" data-testid="toggle-reactions">
            <div className="flex items-center gap-3">
              <ThumbsUp className="w-5 h-5 text-purple-500" />
              <div>
                <Label htmlFor="reactions" className="font-medium">Reactions</Label>
                <p className="text-sm text-muted-foreground">When someone reacts to your post</p>
              </div>
            </div>
            <Switch
              id="reactions"
              checked={preferences.reactions}
              onCheckedChange={(v) => handleToggle('reactions', v)}
            />
          </div>
        </div>
      </div>
      </div>
      </div>
      
      {saving && (
        <div className="fixed bottom-4 right-4 bg-background border border-primary/10 rounded-lg p-3 shadow-lg flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Saving...</span>
        </div>
      )}
    </div>
  );
}
